import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { invalidateQuoteRequestLineDetails } from "#modules/optimizations/infra/invalidateQuoteRequestLineDetails";
import {
  getOptimizationsQueryData,
  optimizationsQuery,
  setOptimizationsQueryData,
} from "#modules/optimizations/infra/optimizations.query";
import { withPreservedCompatibleLines } from "#modules/optimizations/infra/preserveCompatibleLines";
import { removeOptimizationLine } from "#modules/optimizations/infra/removeOptimizationLine.api";
import { quoteRequestLinesQuery } from "#modules/quoteRequestLines/quoteRequestLinesQuery";
import { quoteRequestQuery } from "#modules/quoteRequests/infra/quoteRequest.query";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { sumMachineTimes } from "shared";

export const useRemoveOptimizationLine = ({ quoteRequestId }: { quoteRequestId: string }) => {
  const queryClient = useQueryClient();
  const { queryKey } = optimizationsQuery({ quoteRequestId });

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: removeOptimizationLine,
    onMutate: async ({ optimizationId, lineId }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousOptimization = getOptimizationsQueryData(queryClient, quoteRequestId)?.find(
        (optimization) => optimization.id === optimizationId,
      );

      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) => {
          if (optimization.id !== optimizationId) {
            return optimization;
          }

          const quoteRequestLines = optimization.quoteRequestLines.filter(
            (line) => line.id !== lineId,
          );
          const metadata = optimization.machineTimesOptimizationMetadata;

          if (!metadata) {
            return { ...optimization, quoteRequestLines };
          }

          const resetTimes = sumMachineTimes({
            lines: quoteRequestLines,
            industrialProcess: metadata.companyMachine.machine.industrialProcess,
            companyMachineId: metadata.companyMachineId,
          });

          return {
            ...optimization,
            quoteRequestLines,
            machineTimesOptimizationMetadata: {
              ...metadata,
              ...resetTimes,
            },
          };
        }),
      );

      return { previousOptimization };
    },
    onError: (err: Error, { optimizationId }, context) => {
      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === optimizationId && context?.previousOptimization
            ? context.previousOptimization
            : optimization,
        ),
      );

      showErrorToast({ title: err.message });
      errorHandler(err);
    },
    onSuccess: (updatedOptimization, { lineId }, context) => {
      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === updatedOptimization.id
            ? withPreservedCompatibleLines(optimization, updatedOptimization)
            : optimization,
        ),
      );
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.optimizations.enabledByQuoteRequest(quoteRequestId),
      });
      void queryClient.invalidateQueries(quoteRequestQuery(quoteRequestId));
      void queryClient.invalidateQueries(quoteRequestLinesQuery());
      invalidateQuoteRequestLineDetails(queryClient, [
        lineId,
        ...(context.previousOptimization?.quoteRequestLines.map((line) => line.id) ?? []),
      ]);
    },
  });

  return { removeOptimizationLine: mutate, removeOptimizationLineAsync: mutateAsync, ...rest };
};
